import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Clock, ShieldCheck, Wrench } from "lucide-react";
import { trackCall } from "@/lib/track";
import { SERVICE_PAGES } from "@/pages/ServiceFaqPage";

export const SERVICE_AREAS = {
  moonah: {
    name: "Moonah",
    postcode: "7009",
    drive: "5 minutes",
    distance: "3 km",
    intro:
      "Moonah drivers have been trusting Tassie Automotive with their cars for good reason — we're just five minutes up Main Road at 46 Grove Rd, Glenorchy. Skip the trip into Hobart: drop your car with us in the morning, grab a coffee on Main Road or hop on the bus back home, and we'll text you the moment it's ready.",
    body:
      "Whether you're commuting down the Brooker Highway every day or doing school runs around Albert Road, stop-start driving is tough on brakes, oil and cooling systems. Our Repco Authorised workshop handles logbook servicing that protects your new-car warranty, brake and suspension work, diagnostics, tyres and everything in between — for every make from Toyota and Mazda to BMW and Mercedes-Benz.",
    faqs: [
      { q: "How far is Tassie Automotive from Moonah?", a: "About 3 km — a 5 minute drive up Main Road to 46 Grove Rd, Glenorchy. Easier than heading into the Hobart CBD and parking is free on site." },
      { q: "Can I wait while my Moonah car is serviced?", a: "You're welcome to wait, or drop your keys and head back to Moonah — we'll SMS you the moment your car is ready for pickup." },
    ],
  },
  claremont: {
    name: "Claremont",
    postcode: "7011",
    drive: "10 minutes",
    distance: "7 km",
    intro:
      "For Claremont locals, Tassie Automotive is a straight 10 minute run down Main Road — no need to battle traffic into Hobart for a decent mechanic. We're the Repco Authorised workshop at 46 Grove Rd, Glenorchy, and we look after everything from first services on new cars to keeping older Cadbury-era classics running sweet.",
    body:
      "Claremont's mix of highway commutes and hilly side streets means brakes, cooling systems and suspension take a beating. We do full logbook servicing (your new-car warranty stays intact), brake machining and replacement, cooling system repairs, diagnostics, tyres and pre-purchase inspections. Book online in 60 seconds — pick a day, drop the keys, and track everything from your phone.",
    faqs: [
      { q: "Do you service cars from Claremont and Austins Ferry?", a: "All the time — we're 10 minutes down Main Road from Claremont Village. Many of our regulars come from Claremont, Austins Ferry and Berriedale." },
      { q: "Can I book a Claremont car in online?", a: "Yes — book online in about 60 seconds, no account needed. You'll get an SMS confirmation and a reminder the day before." },
    ],
  },
  montrose: {
    name: "Montrose",
    postcode: "7010",
    drive: "4 minutes",
    distance: "2 km",
    intro:
      "Montrose is practically next door — we're four minutes away at 46 Grove Rd, Glenorchy. Tassie Automotive is the closest Repco Authorised workshop to Montrose, which means proper logbook servicing, honest advice and quality parts without leaving the neighbourhood.",
    body:
      "From the foreshore to Islington Road, Montrose drivers bring us everything: family SUVs due for logbook services, utes needing brakes before the next trip up the mountain, and daily drivers throwing warning lights. We diagnose properly with current scan tools, quote before we touch anything, and stamp your logbook so your manufacturer warranty stays protected.",
    faqs: [
      { q: "Where exactly are you relative to Montrose?", a: "46 Grove Rd, Glenorchy — about 2 km from Montrose Bay, a 4 minute drive. You can even walk back along the bike track while we work on your car." },
      { q: "What services do Montrose customers book most?", a: "Logbook servicing, brakes and general repairs top the list — plus tyres, diagnostics and air-conditioning ahead of summer." },
    ],
  },
  rosetta: {
    name: "Rosetta",
    postcode: "7010",
    drive: "5 minutes",
    distance: "3 km",
    intro:
      "Rosetta locals don't need to go far for a proper mechanic — Tassie Automotive is five minutes away at 46 Grove Rd, Glenorchy. We're the Repco Authorised workshop your neighbours already use for logbook servicing, brakes and everything in between, with online booking that takes about 60 seconds.",
    body:
      "Between the Main Road commute and weekend runs up to Mt Wellington, Rosetta cars work hard. We keep them healthy with manufacturer-schedule logbook servicing (your new-car warranty stays protected), brake and clutch work, cooling system repairs, proper computer diagnostics, tyres and wheel alignments. Every job is quoted up front — no surprises when you pick up the keys.",
    faqs: [
      { q: "How close are you to Rosetta?", a: "About 3 km — a 5 minute drive via Main Road to 46 Grove Rd, Glenorchy. Drop the car in the morning and we'll SMS you when it's ready." },
      { q: "Do you do pink slips / inspections for Rosetta drivers?", a: "We do pre-purchase and general safety inspections — call (03) 6288 7861 and we'll fit you in, usually within a couple of days." },
    ],
  },
  berriedale: {
    name: "Berriedale",
    postcode: "7011",
    drive: "6 minutes",
    distance: "4 km",
    intro:
      "Heading to Mona? Drop your car with us on the way. Tassie Automotive is six minutes from Berriedale at 46 Grove Rd, Glenorchy — a Repco Authorised workshop that treats your car (and your wallet) with respect. Book online, drop the keys, and track the whole job from your phone.",
    body:
      "Berriedale drivers bring us everything from near-new SUVs due their first logbook service to hard-working utes and vans. We handle scheduled servicing that keeps your manufacturer warranty intact, brake machining and replacement, suspension, exhaust, battery and charging systems, air-conditioning and full diagnostic scans — all with quality Repco parts and a written quote before we start.",
    faqs: [
      { q: "Is Tassie Automotive far from Berriedale?", a: "No — about 4 km, a 6 minute drive down Main Road to 46 Grove Rd, Glenorchy. Much closer than heading into Hobart." },
      { q: "Can you look at my car the same week?", a: "Usually yes — book online and pick any open slot. If it's urgent, call (03) 6288 7861 and we'll do our best to squeeze you in." },
    ],
  },
  "new-town": {
    name: "New Town",
    postcode: "7008",
    drive: "9 minutes",
    distance: "6 km",
    intro:
      "New Town drivers: skip the CBD workshops and their parking headaches. Tassie Automotive is a nine minute run up the Brooker at 46 Grove Rd, Glenorchy — Repco Authorised, honestly priced, and easy to book online in about 60 seconds with SMS updates the whole way.",
    body:
      "From daily Brooker Highway commutes to school runs around New Town Road, we keep New Town cars safe and reliable: logbook servicing that protects your new-car warranty, brakes and rotors, steering and suspension, cooling systems, air-conditioning regas, tyres and computer diagnostics for warning lights. European, Japanese or Korean — we service all makes and models.",
    faqs: [
      { q: "Why choose a Glenorchy mechanic over one in the city?", a: "Easier parking, quicker drop-offs and typically sharper pricing — and we're only 9 minutes from New Town via the Brooker Highway." },
      { q: "Do you offer any reminders so I don't forget my booking?", a: "Yes — you'll get an instant SMS and email when you book, a confirmation once we lock it in, and a reminder the day before your slot." },
    ],
  },
};

const TOP_SERVICES = ["Logbook servicing", "Brakes & rotors", "Engine diagnostics", "Suspension & steering", "Tyres & wheel alignment", "Air conditioning"];

export default function ServiceAreaPage() {
  const { suburb } = useParams();
  const area = SERVICE_AREAS[suburb];
  if (!area) return <Navigate to="/" replace />;

  const others = Object.entries(SERVICE_AREAS).filter(([k]) => k !== suburb);

  return (
    <div className="bg-background min-h-screen" data-testid={`area-page-${suburb}`}>
      <section className="relative border-b border-zinc-900 bg-zinc-950/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-red flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5" /> Servicing {area.name} {area.postcode}
          </p>
          <h1 className="mt-4 font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter text-white max-w-3xl">
            Your local mechanic near <span className="text-brand-red">{area.name}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-300 leading-relaxed">{area.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link data-testid="area-book-btn" to="/book" className="inline-flex items-center gap-3 px-7 py-4 bg-brand-red text-white font-display uppercase text-lg tracking-tight hover:bg-[#B91C2B] transition-colors">
              Book your service <ArrowRight className="h-5 w-5" />
            </Link>
            <a data-testid="area-call-btn" href="tel:+61362887861" onClick={() => trackCall(`area_${suburb}`)} className="inline-flex items-center gap-3 px-7 py-4 border border-white/20 text-white font-display uppercase text-lg tracking-tight hover:border-brand-red hover:text-brand-red transition-colors">
              Call (03) 6288 7861
            </a>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 max-w-2xl">
            {[
              { Icon: Clock, label: `${area.drive} away`, sub: `${area.distance} from ${area.name}` },
              { Icon: ShieldCheck, label: "Repco Authorised", sub: "Warranty-safe logbook servicing" },
              { Icon: Wrench, label: "All makes & models", sub: "Cars, SUVs, utes, 4WDs" },
            ].map(({ Icon, label, sub }) => (
              <div key={label} className="bg-zinc-950 p-5">
                <Icon className="h-5 w-5 text-brand-red" />
                <div className="mt-2 text-sm font-semibold text-white">{label}</div>
                <div className="text-xs text-zinc-500">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <h2 className="font-display uppercase text-2xl sm:text-3xl tracking-tight text-white">
            What we do for {area.name} drivers
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">{area.body}</p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {TOP_SERVICES.map((s) => (
              <li key={s} className="flex items-center gap-2 text-sm text-zinc-300">
                <span className="h-1.5 w-1.5 bg-brand-red" /> {s}
              </li>
            ))}
          </ul>
          <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-sm text-brand-red hover:underline">
            See all services & pricing <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-8 border border-zinc-900 bg-zinc-950 p-5" data-testid={`area-service-combos-${suburb}`}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Popular services near {area.name}</p>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5">
              {Object.entries(SERVICE_PAGES).map(([k, s]) => (
                <Link key={k} data-testid={`area-svc-${k}`} to={`/services/${k}`} className="text-sm text-zinc-300 hover:text-brand-red transition-colors">
                  {s.name} near {area.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 space-y-8">
          {area.faqs.map((f) => (
            <div key={f.q} className="border-l-2 border-brand-red pl-5">
              <h3 className="text-white font-semibold">{f.q}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
            </div>
          ))}
          <div className="border border-zinc-900 bg-zinc-950 p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Also servicing</p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {others.map(([k, a]) => (
                <Link key={k} data-testid={`area-link-${k}`} to={`/mechanic/${k}`} className="text-sm text-zinc-300 hover:text-brand-red transition-colors">
                  Mechanic near {a.name}
                </Link>
              ))}
              <Link to="/" className="text-sm text-zinc-300 hover:text-brand-red transition-colors">Glenorchy (home base)</Link>
            </div>
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          // Static allowlisted data only; escape "<" as defence-in-depth against script breakout
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            name: "Tassie Automotive",
            telephone: "+61362887861",
            address: { "@type": "PostalAddress", streetAddress: "46 Grove Rd", addressLocality: "Glenorchy", addressRegion: "TAS", postalCode: "7010", addressCountry: "AU" },
            areaServed: [area.name, "Glenorchy"],
            url: `https://tassieautomotive.com.au/mechanic/${suburb}`,
          }).replace(/</g, "\\u003c"),
        }}
      />
    </div>
  );
}
